import { Dropdown, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { CgProfile } from 'react-icons/cg'
import { FiLogOut } from 'react-icons/fi'
import { LuMoon, LuSun } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'

import { userManager } from '@/Core/Managers'
import { GetRouteUrlByKeyTask } from '@/Core/Tasks/UI/Menu/GetRouteUrlByKeyTask'
import { CurrentUserAvatar, currentUserName } from '@/Ship/Components/CurrentUser'
import { switchTheme } from '@/Ship/Components/Theme'
import { classNames } from '@/Ship/Helpers/classNames'
import { layoutManager } from '@/Ship/Stores/LayoutManager'
import { themeManager } from '@/Ship/Stores/ThemeManager'
import { LogoutTask } from '@/Ship/Tasks/LogoutTask'

import type { MenuProps } from 'antd'

import styles from './UserBlock.module.css'

/** До якої ширини шапка вузька: лише аватар, ПІБ і посада — у підказці (`SPEC-014` §1.2). */
const NARROW_QUERY = '(max-width: 1024px)'

const useIsNarrow = (): boolean => {
    const [isNarrow, setIsNarrow] = useState(() => window.matchMedia?.(NARROW_QUERY).matches ?? false)

    useEffect(() => {
        const query = window.matchMedia?.(NARROW_QUERY)
        if (!query) return

        const onChange = (event: MediaQueryListEvent) => setIsNarrow(event.matches)
        query.addEventListener('change', onChange)

        return () => query.removeEventListener('change', onChange)
    }, [])

    return isNarrow
}

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

/**
 * Блок користувача — правий край шапки (`SPEC-014` §1.2, карта UI «В»).
 *
 * Аватар (фото або ініціали «Прізвище Ім'я»), ПІБ і посада; клік відкриває
 * меню «Мій профіль» / «Вийти». Замінює дві окремі кнопки PD «Профіль» і
 * «Вихід».
 *
 * - Меню відкривається кліком, а не наведенням: сенсорні екрани (В8).
 * - «Мій профіль» — саме `Setting.Profile`: там вкладка «Інтерфейс»
 *   (`development.md`, «Екран перевіряють тим шляхом…»).
 * - «Вийти» відкликає refresh-токен (`LogoutTask`, D-25).
 * - На ≤ 670 px перемикач теми з шапки переїжджає сюди (§1.4).
 *
 * `observer` над `userManager.user`: нове фото чи ПІБ із профілю видно тут
 * одразу, без перезавантаження (С2.1).
 */
export const UserBlock = observer(() => {
    const navigate = useNavigate()
    const isNarrow = useIsNarrow()

    const user = userManager.user
    const name = currentUserName(user)
    const position = text(user['position'])
    const themeLabel = themeManager.isDark ? 'Світла тема' : 'Темна тема'

    const items: MenuProps['items'] = [
        { key: 'profile', icon: <CgProfile />, label: 'Мій профіль' },
        ...(layoutManager.isMobile
            ? [
                  {
                      key: 'theme',
                      icon: themeManager.isDark ? <LuSun strokeWidth={1.5} /> : <LuMoon strokeWidth={1.5} />,
                      label: themeLabel,
                  },
              ]
            : []),
        { type: 'divider' as const },
        { key: 'logout', icon: <FiLogOut />, label: 'Вийти', danger: true },
    ]

    const onClick: MenuProps['onClick'] = ({ key, domEvent }) => {
        if (key === 'profile') {
            const url = GetRouteUrlByKeyTask('Setting.Profile')

            if (url) navigate(url)
        }

        if (key === 'theme') {
            // Коло нової теми розходиться від пункту меню, як від кнопки в шапці
            switchTheme(domEvent.currentTarget as Element)
        }

        if (key === 'logout') void LogoutTask()
    }

    const compact = isNarrow || layoutManager.isMobile

    const trigger = (
        <button
            type="button"
            className={classNames(styles['trigger'], compact && styles['triggerCompact'])}
            aria-label="Меню користувача"
        >
            <CurrentUserAvatar size={32} />
            {!compact && (
                <span className={classNames(styles['text'])}>
                    <span className={classNames(styles['name'])}>{name}</span>
                    {position && <span className={classNames(styles['position'])}>{position}</span>}
                </span>
            )}
        </button>
    )

    return (
        <Dropdown menu={{ items, onClick }} trigger={['click']} placement="bottomRight">
            {compact ? (
                <Tooltip
                    title={
                        <>
                            <div>{name}</div>
                            {position && <div>{position}</div>}
                        </>
                    }
                    placement="bottomRight"
                >
                    {trigger}
                </Tooltip>
            ) : (
                trigger
            )}
        </Dropdown>
    )
})

UserBlock.displayName = 'UserBlock'
